<script>
import groupBy from 'lodash/groupBy'
import ActionButton from '@/components/ui/buttons/ActionButton.vue'
import ActionButtonItem from '@/components/ui/buttons/ActionButtonItem.vue'
import { nanoid } from 'nanoid'
import TaskTemplateGroup from '@/components/tasks/form/TaskTemplateGroup.vue'
import TaskTemplateTask from '@/components/tasks/form/TaskTemplateTask.vue'
import DragSortableContainer from '@/components/ui/DragSortableContainer.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import FeatureSelect from '@/components/cars/edit/FeatureSelect.vue'
import FeatureToggle from '@/components/cars/edit/FeatureToggle.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import { mapGetters } from 'vuex'
import TaskTemplateTriggerCarTypeField from '@/components/tasks/form/TaskTemplateTriggerCarTypeField.vue'
import TaskTemplateTriggerRegistrationTypeField from '@/components/tasks/form/TaskTemplateTriggerRegistrationTypeField.vue'
import TaskTemplateTriggerSalesChannelField from '@/components/tasks/form/TaskTemplateTriggerSalesChannelField.vue'
import TaskTemplateTriggerPaymentMethodField from '@/components/tasks/form/TaskTemplateTriggerPaymentMethodField.vue'
import TaskTemplateTriggerDispatchTypeField from '@/components/tasks/form/TaskTemplateTriggerDispatchTypeField.vue'
import TaskTemplateTriggerStatusField from '@/components/tasks/form/TaskTemplateTriggerStatusField.vue'
import TaskTemplateTriggerProductiondataOrderStatusField from '@/components/tasks/form/TaskTemplateTriggerProductiondataOrderStatusField.vue'
import TaskTemplateTriggerDispatchAssigneeField from '@/components/tasks/form/TaskTemplateTriggerDispatchAssigneeField.vue'
import MakeSelect from '@/components/cars/inputs/MakeSelect.vue'
import { getMakes, getProductiondataSellCategories } from '@/api/cars'
import MultiSelectHint from '@/components/ui/MultiSelectHint.vue'
import safe from 'safe-await'
import uniq from 'lodash/uniq'

const TRIGGER_FIELDS = {
  item_type: 'mia-task-template-trigger-car-type-field',
  item_registration_type: 'mia-task-template-trigger-registration-type-field',
  item_saleschannel: 'mia-task-template-trigger-sales-channel-field',
  item_paymethod: 'mia-task-template-trigger-payment_method-field',
  item_dispatch_type: 'mia-task-template-trigger-dispatch-type-field',
  item_status: 'mia-task-template-trigger-status-field',
  item_productiondata_order_status: 'mia-task-template-trigger-productiondata-order-status-field',
  item_dispatch_assignee: 'mia-task-template-trigger-dispatch-assignee-field'
}

export default {
  components: {
    'mia-action-button-item': ActionButtonItem,
    'mia-action-button': ActionButton,
    'mia-drag-sortable-container': DragSortableContainer,
    'mia-feature-input': FeatureInput,
    'mia-feature-toggle': FeatureToggle,
    'mia-feature-select': FeatureSelect,
    'mia-make-select': MakeSelect,
    'mia-multi-select-hint': MultiSelectHint,
    'mia-task-template-group': TaskTemplateGroup,
    'mia-task-template-task': TaskTemplateTask,
    'mia-task-template-trigger-car-type-field': TaskTemplateTriggerCarTypeField,
    'mia-task-template-trigger-registration-type-field': TaskTemplateTriggerRegistrationTypeField,
    'mia-task-template-trigger-sales-channel-field': TaskTemplateTriggerSalesChannelField,
    'mia-task-template-trigger-payment_method-field': TaskTemplateTriggerPaymentMethodField,
    'mia-task-template-trigger-dispatch-type-field': TaskTemplateTriggerDispatchTypeField,
    'mia-task-template-trigger-status-field': TaskTemplateTriggerStatusField,
    'mia-task-template-trigger-productiondata-order-status-field': TaskTemplateTriggerProductiondataOrderStatusField,
    'mia-task-template-trigger-dispatch-assignee-field': TaskTemplateTriggerDispatchAssigneeField
  },

  mixins: [HandlesBackendValidationErrors],

  model: {
    prop: 'template',
    event: 'update:template'
  },

  props: {
    template: {
      type: Object,
      required: true
    },

    errorsKey: {
      type: String,
      required: true
    },

    highlightedTaskId: {
      default: null,
      validator: value => value === null || value === undefined || ['string', 'number'].includes(typeof value)
    }
  },

  data: () => ({
    productiondataSellCategories: {},
    productiondataSellCategoriesLoaded: false,
    makes: []
  }),

  computed: {
    ...mapGetters('tasks', ['triggers']),
    ...mapGetters(['carTypes']),

    backendValidationErrorsKey() {
      return this.errorsKey
    },

    groupedTriggers() {
      return groupBy(this.triggers, 'item_group')
    },

    taskGroups() {
      return this.template.tasks.filter(task => task.item_type === 'group')
    },

    ungroupedTasks() {
      return this.template.tasks.filter(task => task.item_type === 'task' && !task.item_group)
    },

    selectedTrigger() {
      return this.triggers.find(trigger => trigger.item_event === this.template.item_trigger)
    },

    hasAdditionalTriggerConfig() {
      if (!this.selectedTrigger) {
        return false
      }

      return this.selectedTrigger.item_context && this.selectedTrigger.item_context.length > 0
    },

    triggerContext() {
      return Array.isArray(this.template.item_trigger_context) ? {} : this.template.item_trigger_context
    },

    hasProductiondataSellCategories() {
      return Object.keys(this.productiondataSellCategories).length > 0
    },

    makesProductionDataSellCategories() {
      if (!this.hasProductiondataSellCategories) {
        return {}
      }

      if (this.template.item_makes.length === 0) {
        return this.productiondataSellCategories
      }

      return this.template.item_makes.reduce((acc, makeId) => {
        if (this.productiondataSellCategories[makeId] !== undefined) {
          acc[makeId] = this.productiondataSellCategories[makeId]
        }

        return acc
      }, {})
    }
  },

  created() {
    this.$store.dispatch('tasks/loadTriggers')
    this.$store.dispatch('tasks/loadAutoAssignRules')
    this.$store.dispatch('tasks/loadInputFields')
    this.loadProductiondataSellCategories()
    this.$store.dispatch('loadCarTypes')
    this.loadCarMakes()
  },

  methods: {
    async loadCarMakes() {
      const [error, makes] = await safe(getMakes())

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Hersteller konnten nicht geladen werden']
        })
        return
      }

      this.makes = makes
    },

    addTask(type = 'task') {
      const newTask = {
        id: nanoid(),
        item_title: `Neue ${type === 'group' ? 'Gruppe' : 'Aufgabe'}`,
        item_description: '',
        item_type: type,
        item_group: null,
        item_sort: Math.max(0, this.template.tasks.length - 1),
        item_auto_assign: 'N',
        item_auto_assign_rule: null,
        item_input_fields: [],
        item_department_id: null,
        item_inform_department_head: '0',
        item_deadline: 'N',
        item_deadline_unit: null,
        item_deadline_value: null,
        item_time_value: 0,
        item_priority: 0,
        item_tasks_required: [],
        item_unique: 'N',
        item_idle: 'N',
        item_idle_unit: null,
        item_idle_value: null
      }

      this.$emit('update:template', {
        ...this.template,
        tasks: [
          ...this.template.tasks,
          newTask
        ]
      })

      this.$refs.createActionButtonRef.close()
    },

    getTasksInGroup(group) {
      return this.template.tasks.filter(task => task.item_type === 'task' && task.item_group === group.id)
    },

    onTitleInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_title: value
      })

      this.clearErrors('item_title')
    },

    onGroupInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_group: value
      })

      this.clearErrors('item_group')
    },

    onActiveInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_active: value ? '1' : '0'
      })

      this.clearErrors('item_active')
    },

    onActiveFromInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_active_from: value
      })

      this.clearErrors('item_active_from')
    },

    onActiveToInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_active_to: value
      })

      this.clearErrors('item_active_to')
    },

    onMakesInput(event) {
      const value = Array.from(event.target.selectedOptions).map(option => option.value)

      this.$emit('update:template', {
        ...this.template,
        item_makes: value
      })

      this.clearErrors('item_makes')
    },

    onProductiondataSellCategoriesChange(event) {
      const value = Array.from(event.target.selectedOptions).map(option => option.value)

      this.$emit('update:template', {
        ...this.template,
        item_productiondata_sell_categories: uniq(value)
      })

      this.clearErrors('item_productiondata_sell_categories')
    },

    onTriggerInput(value) {
      this.$emit('update:template', {
        ...this.template,
        item_trigger: value
      })

      this.clearErrors('item_trigger')
    },

    onCarTypesInput(event) {
      const value = Array.from(event.target.selectedOptions).map(option => option.value)

      this.$emit('update:template', {
        ...this.template,
        item_car_types: value
      })

      this.clearErrors('item_car_types')
    },

    updateTask(updatedTask) {
      this.$emit('update:template', {
        ...this.template,
        tasks: this.template.tasks.map((task) => {
          if (task.id === updatedTask.id) {
            return {
              ...task,
              ...updatedTask
            }
          }

          return task
        })
      })

      this.$nextTick(() => {
        this.updateDraggable()
      })
    },

    addTaskToGroup(task, group) {
      this.updateTask({
        ...task,
        item_group: group.id
      })
    },

    removeTaskFromGroup(task) {
      this.updateTask({
        ...task,
        item_group: null
      })
    },

    updateDraggable() {
      this.$refs.dragSortableContainerRef.update()
    },

    updateSorting() {
      this.updateTopLevelSorting()

      this.$nextTick(() => {
        this.updateSortingInGroups()

        this.$nextTick(() => {
          this.updateDraggable()
        })
      })
    },

    getTopLevelNodes() {
      return Array.from(this.$refs.dragSortableContainerRef.$el.querySelectorAll('[data-task-id], [data-group-id]'))
    },

    getGroupNodes() {
      return Array.from(this.$refs.dragSortableContainerRef.$el.querySelectorAll('[data-group-id]'))
    },

    updateTopLevelSorting() {
      const taskNodes = this.getTopLevelNodes()
      const sortMap = this.template.tasks.reduce((acc, taskOrGroup) => {
        if (taskOrGroup.item_type === 'group' || taskOrGroup.item_group === null) {
          acc[taskOrGroup.id] = 0
        }

        return acc
      }, {})

      const topLevelNodes = taskNodes.filter((taskOrGroupNode) => {
        const itemId = taskOrGroupNode.dataset.taskId || taskOrGroupNode.dataset.groupId

        if (itemId === undefined) {
          return false
        }

        return sortMap[itemId] !== undefined
      })

      topLevelNodes.forEach((taskNode, index) => {
        sortMap[taskNode.dataset.taskId || taskNode.dataset.groupId] = index
      })

      this.$emit('update:template', {
        ...this.template,
        tasks: this.template.tasks.map((task) => {
          if (sortMap[task.id] === undefined) {
            return task
          }

          return {
            ...task,
            item_sort: sortMap[task.id]
          }
        })
      })
    },

    updateSortingInGroups() {
      const groupNodes = this.getGroupNodes()
      const sortMap = {}

      groupNodes.forEach((groupNode) => {
        Array.from(groupNode.querySelectorAll('[data-task-id]')).forEach((taskNode, index) => {
          sortMap[taskNode.dataset.taskId] = index
        })
      })

      this.$emit('update:template', {
        ...this.template,
        tasks: this.template.tasks.map((task) => {
          if (sortMap[task.id] === undefined) {
            return task
          }

          return {
            ...task,
            item_sort: sortMap[task.id]
          }
        })
      })
    },

    onDeleteTask(taskToDelete) {
      this.$emit('update:template', {
        ...this.template,
        tasks: this.template.tasks.filter(task => task.id !== taskToDelete.id)
      })

      this.$nextTick(() => {
        this.updateSorting()
      })
    },

    onDeleteGroup(groupToDelete) {
      this.$emit('update:template', {
        ...this.template,
        tasks: this.template.tasks.filter(task => task.id !== groupToDelete.id && task.item_group !== groupToDelete.id)
      })

      this.$nextTick(() => {
        this.updateSorting()
      })
    },

    onUpdateGroup(updatedGroup) {
      this.updateTask(updatedGroup)
    },

    onUpdateTask(updatedTask) {
      this.updateTask(updatedTask)
    },

    onTaskDropped(event) {
      if (event.target.dataset.groupId || event.source.dataset.groupId) {
        this.onTaskDroppedInGroup(event)
      }

      this.$nextTick(() => {
        this.updateSorting()
      })
    },

    onTaskDroppedInGroup(event) {
      const task = this.template.tasks.find(t => t.item_type === 'task' && t.id === event.el.dataset.taskId)

      // Dropped out of a group to general tasks
      if (!event.target.dataset.groupId) {
        this.removeTaskFromGroup(task)
        return
      }

      // Dropped from into a group
      const group = this.template.tasks.find(t => t.item_type === 'group' && t.id === event.target.dataset.groupId)

      this.addTaskToGroup(task, group)
    },

    expandAll() {
      this.$refs.taskTemplateRef.forEach(taskTemplateRef => taskTemplateRef.expand())
    },

    collapseAll() {
      this.$refs.taskTemplateRef.forEach(taskTemplateRef => taskTemplateRef.collapse())
    },

    getTriggerFieldComponent(field) {
      return TRIGGER_FIELDS[field]
    },

    onTriggerFieldChange(field, value) {
      const currentTriggerContext = Array.isArray(this.template.item_trigger_context) ? {} : this.template.item_trigger_context

      this.$emit('update:template', {
        ...this.template,
        item_trigger_context: {
          ...currentTriggerContext,
          fields: (currentTriggerContext.fields || []).filter(currentField => currentField.id !== field).concat([{
            id: field,
            value
          }])
        }
      })
    },

    getTriggerFieldValue(field) {
      const currentField = (this.triggerContext.fields || []).find(f => f.id === field)

      return currentField ? currentField.value : null
    },

    async loadProductiondataSellCategories() {
      const [error, categories] = await getProductiondataSellCategories()
      this.productiondataSellCategoriesLoaded = true

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Verkaufskategorien konnten nicht geladen werden']
        })
        return
      }

      this.productiondataSellCategories = categories
    },

    getMakeName(make) {
      return make
        .trim()
        .replace(/\s+/g, ' ')
        .split(' ')
        .map(part => `${part[0].toUpperCase()}${part.toLowerCase().substring(1)}`)
        .join(' ')
    }
  }
}
</script>

<template>
  <div>
    <div class="space-y-6">
      <div class="bg-gray-100 border border-gray-200 rounded shadow-xs py-3 px-4">
        <h2 class="my-0 py-0 text-xl pb-1 border-b border-gray-300 tracking-normal">
          Grundeinstellungen
        </h2>
        <div class="mt-4">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <mia-feature-input
                label="Name der Vorlage"
                :value="template.item_title"
                :errors="getErrors('item_title')"
                autofocus
                @input="onTitleInput"
              />
            </div>
            <div />
            <div>
              <mia-feature-input
                label="Name der Gruppe"
                :value="template.item_group"
                :errors="getErrors('item_group')"
                autofocus
                @input="onGroupInput"
              />
            </div>

            <div v-if="triggers.length > 0">
              <mia-feature-select
                :value="template.item_trigger"
                :errors="getErrors('item_trigger')"
                label="Trigger"
                @input="onTriggerInput"
              >
                <template #options>
                  <option value="">
                    Trigger auswählen
                  </option>
                  <optgroup
                    v-for="(triggers, triggerGroup) in groupedTriggers"
                    :key="triggerGroup"
                    :label="triggerGroup"
                  >
                    <option
                      v-for="trigger in triggers"
                      :key="trigger.id"
                      :value="trigger.item_event"
                    >
                      {{ trigger.item_title }}
                    </option>
                  </optgroup>
                </template>
              </mia-feature-select>

              <div
                v-if="hasAdditionalTriggerConfig"
                class="mt-4 grid grid-cols-2 gap-2"
              >
                <div
                  v-for="field in selectedTrigger.item_context"
                  :key="field"
                  :class="{
                    'col-span-2': getTriggerFieldComponent(field) === 'mia-task-template-trigger-productiondata-order-status-field'
                  }"
                >
                  <component
                    :is="getTriggerFieldComponent(field)"
                    :value="getTriggerFieldValue(field)"
                    @change="onTriggerFieldChange(field, $event)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gray-100 border border-gray-200 rounded shadow-xs py-3 px-4">
        <h2 class="my-0 py-0 text-xl pb-1 border-b border-gray-300 tracking-normal">
          Filterkriterien
        </h2>
        <div class="mt-4">
          <div class="grid grid-cols-2 gap-6">
            <div>
              <mia-feature-input
                label="Aktiv von"
                :value="template.item_active_from"
                :errors="getErrors('item_active_from')"
                type="datetime-local"
                @input="onActiveFromInput"
              />
            </div>

            <div>
              <mia-feature-input
                label="Aktiv bis"
                :value="template.item_active_to"
                :errors="getErrors('item_active_to')"
                type="datetime-local"
                @input="onActiveToInput"
              />
            </div>

            <div class="col-span-2">
              <mia-feature-toggle
                :value="template.item_active === '1'"
                label="Aktiv"
                label-position="top"
                :errors="getErrors('item_active')"
                @input="onActiveInput"
              />
            </div>

            <div>
              <label class="mb-1 mr-2 tracking-wider text-gray-800">Hersteller</label>
              <select
                v-if="makes.length > 0"
                class="w-full form-select"
                multiple
                size="10"
                @input="onMakesInput"
              >
                <option
                  v-for="make in makes"
                  :key="make.name"
                  :value="make.name"
                  :selected="template.item_makes.includes(make.name)"
                >
                  {{ make.description }}
                </option>
              </select>
              <mia-multi-select-hint />
            </div>

            <div>
              <label class="mb-1 mr-2 tracking-wider text-gray-800">Verkaufskategorie</label>
              <select
                v-if="productiondataSellCategoriesLoaded"
                class="w-full form-select"
                multiple
                :disabled="!hasProductiondataSellCategories || Object.keys(makesProductionDataSellCategories).length === 0"
                size="10"
                @change="onProductiondataSellCategoriesChange"
              >
                <optgroup
                  v-for="(categories, make) in makesProductionDataSellCategories"
                  :key="make"
                  :label="getMakeName(make)"
                >
                  <option
                    v-for="category in categories"
                    :key="category.id"
                    :value="category.id || ''"
                    :selected="template.item_productiondata_sell_categories.includes(category.id || '')"
                  >
                    {{ category.item_label }}
                  </option>
                </optgroup>
              </select>
              <mia-multi-select-hint />
            </div>

            <div>
              <label class="mb-1 mr-2 tracking-wider text-gray-800">Fahrzeugtyp</label>
              <select
                v-if="carTypes.length > 0"
                class="w-full form-select"
                multiple
                size="6"
                @input="onCarTypesInput"
              >
                <option
                  v-for="carType in carTypes"
                  :key="carType.id"
                  :value="carType.id"
                  :selected="template.item_car_types.includes(carType.id)"
                >
                  {{ carType.label }}
                </option>
              </select>
              <mia-multi-select-hint />
            </div>
          </div>
        </div>
      </div>

      <div class="bg-gray-100 border border-gray-200 rounded shadow-xs py-3 px-4">
        <h2 class="my-0 py-0 text-xl pb-1 border-b border-gray-300 tracking-normal flex justify-between items-center">
          <div>
            <span>Aufgaben</span>

            <template v-if="template.tasks.length > 0">
              <button
                class="ml-4 text-sm text-orange-mia hover:underline focus:outline-none"
                type="button"
                @click="collapseAll"
              >
                Alle einklappen
              </button>
              <button
                class="ml-2 text-sm text-orange-mia hover:underline focus:outline-none"
                type="button"
                @click="expandAll"
              >
                Alle aufklappen
              </button>
            </template>
          </div>

          <div>
            <mia-action-button
              ref="createActionButtonRef"
              label="Neu"
            >
              <mia-action-button-item @click="addTask('task')">
                Aufgabe
              </mia-action-button-item>
              <mia-action-button-item @click="addTask('group')">
                Gruppe
              </mia-action-button-item>
            </mia-action-button>
          </div>
        </h2>
        <div class="mt-4">
          <mia-drag-sortable-container
            ref="dragSortableContainerRef"
            v-slot="{ dragHandleClass, dragContainerClass }"
            class="mt-4 space-y-4"
            @drop="onTaskDropped"
          >
            <div
              class="mb-6 space-y-2"
              :class="dragContainerClass"
            >
              <div
                v-if="ungroupedTasks.length === 0"
                class="p-6 border border-gray-200"
              >
                Noch keine Aufgaben vorhanden
              </div>
              <mia-task-template-task
                v-for="task in ungroupedTasks"
                :key="task.id"
                ref="taskTemplateRef"
                :task="task"
                :tasks="ungroupedTasks"
                draggable
                :drag-handle-class="dragHandleClass"
                :highlight="highlightedTaskId !== null && parseInt(highlightedTaskId, 10) === parseInt(task.id, 10)"
                @update:task="onUpdateTask"
                @delete-task="onDeleteTask(task)"
              />
            </div>

            <mia-task-template-group
              v-for="group in taskGroups"
              :key="group.id"
              :group="group"
              draggable
              :drag-handle-class="dragHandleClass"
              @update:group="onUpdateGroup"
              @delete-group="onDeleteGroup(group)"
            >
              <div
                class="m-4 mt-8 border-2 border-dashed border-gray-400 rounded-sm p-4 space-y-4"
                :class="dragContainerClass"
                :data-group-id="group.id"
              >
                <div
                  v-if="getTasksInGroup(group).length === 0"
                  class="text-center"
                >
                  Noch keine Aufgaben in dieser Gruppe
                </div>

                <mia-task-template-task
                  v-for="task in getTasksInGroup(group)"
                  :key="task.id"
                  ref="taskTemplateRef"
                  :task="task"
                  draggable
                  :drag-handle-class="dragHandleClass"
                  :highlight="highlightedTaskId !== null && parseInt(highlightedTaskId, 10) === parseInt(task.id, 10)"
                  @update:task="onUpdateTask"
                  @delete-task="onDeleteTask(task)"
                />
              </div>
            </mia-task-template-group>
          </mia-drag-sortable-container>
        </div>
      </div>
    </div>
  </div>
</template>
